import type {
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Citation } from '@/api/runs';
import { linkLegacyReferences, type DocumentLink } from '@/utils/messageParser';
import { runMarks } from '../runs/runStore';
import { openLegacyDocumentLink } from './legacyLinks';
import { CITATION_CLASS, TABLE_COPY_CLASS, renderAnswer } from './renderAnswer';
import { requestOpenSource } from './sourceEvents';
import styles from './answer.module.css';

interface AnswerBodyProps {
  text: string;
  /** Still arriving: hold back incomplete ends and throttle parsing. */
  streaming: boolean;
  /** Cut short (stopped, interrupted): hold back an incomplete end. */
  partial?: boolean;
  legacy: boolean;
  citations: Citation[] | null;
  messageId: string | number | null;
  runId: string | null;
  /** Document links for old answers' reference markers. */
  links?: DocumentLink[];
  /** Copy one table (by its position); adds a button above each table. */
  onCopyTable?: (index: number) => void;
  /** A comparison result: kit table width and tone badges. */
  comparison?: boolean;
}

/**
 * How often a streamed answer is parsed again: every delta while it is
 * short, then less often as it grows, so long answers stay cheap.
 */
export const renderInterval = (length: number) =>
  length <= 4000 ? 40 : Math.min(500, Math.round(length / 40));

const mark = (name: string) => {
  try {
    if (performance.getEntriesByName(name).length === 0) performance.mark(name);
  } catch {
    // performance.mark is optional.
  }
};

interface Tip {
  n: number;
  name: string;
  left: number;
  top: number;
}

/**
 * The sanitized answer HTML. Mapped `[n]` markers are chips that show the
 * source name on hover and focus and ask for the source panel when
 * activated.
 */
const AnswerBody = ({
  text,
  streaming,
  partial = false,
  legacy,
  citations,
  messageId,
  runId,
  links,
  onCopyTable,
  comparison = false,
}: AnswerBodyProps) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState<Tip | null>(null);

  const source = useMemo(
    () =>
      legacy && links?.length && messageId != null
        ? linkLegacyReferences(text, messageId, links)
        : text,
    [legacy, links, messageId, text],
  );

  // Tables can be copied once they stopped growing.
  const copyTable = !streaming && !!onCopyTable;
  const render = useMemo(
    () => (value: string, isStreaming: boolean) =>
      renderAnswer(value, {
        streaming: isStreaming,
        legacy,
        citations,
        comparison,
        labels: {
          table: t('v2.messages.tableRegion'),
          citation: (n, name) => t('v2.messages.citeLabel', { n, name }),
          copyTable: copyTable ? t('v2.messages.copyTable') : undefined,
        },
      }),
    [citations, comparison, copyTable, legacy, t],
  );

  // Finished answers render once; streamed ones on a throttle.
  const settled = useMemo(
    () => (streaming ? '' : render(source, partial)),
    [partial, render, source, streaming],
  );
  const [streamed, setStreamed] = useState(() =>
    streaming ? render(source, true) : '',
  );
  const lastRender = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(source);
  latest.current = source;

  useEffect(() => {
    if (!streaming) {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
      return;
    }
    const run = () => {
      timer.current = null;
      lastRender.current = Date.now();
      setStreamed(render(latest.current, true));
    };
    const wait =
      renderInterval(source.length) - (Date.now() - lastRender.current);
    if (wait <= 0) {
      run();
    } else if (!timer.current) {
      timer.current = setTimeout(run, wait);
    }
  }, [render, source, streaming]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const html = streaming ? streamed : settled;

  useEffect(() => {
    if (!runId || !html) return;
    const frame = requestAnimationFrame(() => mark(runMarks.firstPaint(runId)));
    return () => cancelAnimationFrame(frame);
  }, [html, runId]);

  const chipOf = (target: EventTarget | null) =>
    target instanceof Element
      ? (target.closest(`.${CITATION_CLASS}`) as HTMLElement | null)
      : null;

  const showTip = (chip: HTMLElement | null) => {
    const box = ref.current;
    if (!chip || !box) {
      setTip(null);
      return;
    }
    const n = Number(chip.dataset.cite);
    const citation = citations?.find(item => Number(item.n) === n);
    const chipRect = chip.getBoundingClientRect();
    const boxRect = box.getBoundingClientRect();
    setTip({
      n,
      name: citation?.name ?? '',
      left: chipRect.left - boxRect.left + chipRect.width / 2,
      top: chipRect.top - boxRect.top,
    });
  };

  const onClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    const copyButton =
      event.target instanceof Element
        ? event.target.closest(`.${TABLE_COPY_CLASS}`)
        : null;
    if (copyButton instanceof HTMLElement && onCopyTable) {
      onCopyTable(Number(copyButton.dataset.table));
      return;
    }
    const chip = chipOf(event.target);
    if (chip) {
      const n = Number(chip.dataset.cite);
      requestOpenSource({
        messageId,
        runId,
        n,
        citation: citations?.find(item => Number(item.n) === n) ?? null,
        opener: chip,
      });
      return;
    }
    if (!legacy) return;
    const link =
      event.target instanceof Element ? event.target.closest('a') : null;
    const href = link?.getAttribute('href');
    if (href && openLegacyDocumentLink(href)) event.preventDefault();
  };

  return (
    <div className={styles.answerWrap}>
      <div
        ref={ref}
        className={styles.answer}
        data-testid="chat-answer-text"
        data-streaming={streaming || undefined}
        onClick={onClick}
        onMouseOver={event => showTip(chipOf(event.target))}
        onMouseLeave={() => setTip(null)}
        onFocus={(event: ReactFocusEvent) => showTip(chipOf(event.target))}
        onBlur={() => setTip(null)}
        onKeyDown={(event: ReactKeyboardEvent) => {
          if (event.key === 'Escape') setTip(null);
        }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {tip ? (
        <span
          className={styles.citeTip}
          style={{ left: tip.left, top: tip.top }}
          aria-hidden="true"
          data-testid="citation-tooltip"
        >
          [{tip.n}] {tip.name}
        </span>
      ) : null}
    </div>
  );
};

export default memo(AnswerBody);
